import type { NextPage } from 'next';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import { Product, HycProduct, IgcProduct, AbsProduct } from '@/creditflowresearch/domain/Product';
import { Market, UsMarket } from '@/creditflowresearch/domain/Market';
import { CORPORATE_SEGMENTS } from '@/creditflowresearch/domain/Segment';
import withApplicationPropsV2 from '@/creditflowresearch/application/withApplicationPropsV2';
import { COMMON_SCREENER_DICTIONARIES } from '@/creditflowresearch/application/Dictionaries/DictionaryRegistry';
import PageFiltersContextProvider from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { PageContentLayout } from '@/creditflowresearch/ui/common/components/ContentLayouts';
import CorporateBondScreenerPage from '@/creditflowresearch/ui/components/corporate-bond-screener';
import { checkAccessByProducts } from '@/creditflowresearch/application/User/access';
import getServerSideCorporateBondScreenerTranches from '@/creditflowresearch/application/CorporateBondScreener/serverSide/getServerSideCorporateBondScreenerTranches';
import getServerSideBondscreenerBreadcrumbs
  from '@/creditflowresearch/application/Breadcrumbs/serverSide/getServerSideBondscreenerBreadcrumbs';
import getServerSideBondScreenerConfig
  from '@/creditflowresearch/application/BondScreener/serverSide/getServerSideBondScreenerConfig';
import getServerSideBondScreenerViews
  from '@/creditflowresearch/application/BondScreener/serverSide/getServerSideBondScreenerViews';
import { pickInitialBondScreenerColumnsAndFilters } from '@/creditflowresearch/application/CorporateBondScreener/pickInitialBondScreenerColumnsAndFilters';
import { pageRedirectTo } from '@framework/router/Next/helpers';
import { addQueryParams, getQueryString } from '@framework/router/nextRouter';
import getDefaultQueryFilters from '@/creditflowresearch/application/helpers/getDefaultQueryFilters';
import { corporateValidators } from '@/creditflowresearch/application/helpers/validators';
import URL from '@/creditflowresearch/Router/urlConstants';
import type { ICorporateBondScreenerTrancheCollectionResponse } from '@/creditflowresearch/domain/vo/Bondscreener/ICorporateBondScreenerTranche';
import type { IBondScreenerConfig } from '@/creditflowresearch/domain/vo/Bondscreener/IBondScreenerConfig';
import type { IBondScreenerViewsResponse } from '@/creditflowresearch/domain/vo/Bondscreener/IBondScreenerView';
import type { TFilterPayload } from '@/creditflowresearch/domain/vo/Bondscreener';
import { isAccessGranted } from '@/creditflowresearch/application/User/access/grants/resolveGrant';
import { EuropeanMarket } from '@/creditflowresearch/domain/Market';
import type { IDictionaryIndustry } from '@/creditflowresearch/domain/vo/IDictionary';

export const getServerSideProps = withApplicationPropsV2(async (context) => {
  const { userPlanState, query, req, globalProps, pageDictionaries } = context;
  const { products: accessibleProducts, regions, plan } = userPlanState;
  const productIds = Product.getIds(query.products as string);
  const marketIds = Market.getIds(query.markets as string);
  const regionIds = Market.getRegionsByMarkets(marketIds).map((region) => region.getId());

  const featureToggles = globalProps.featuresConfig;
  const granted = isAccessGranted(
    { regionIds, productIds, pathname: req.url || '' },
    (toggle) => featureToggles[toggle]?.isEnabled ?? false,
  );
  const isOnlyAbsAccess = Product.areAllOfType(accessibleProducts, AbsProduct.TYPE);
  // TODO: remove when the European market opening period ends; without it the screener
  // is never restricted to the European market
  const isRestrictedToEuropeanMarket =
    UserPlan.isUnauthorized(plan) || UserPlan.isStarter(plan) || isOnlyAbsAccess;

  const conditions = [
    granted || UserPlan.isPro(plan),
    granted || !query.products || checkAccessByProducts(productIds, accessibleProducts),
  ];
  // views are per-user; the promo grant opens the screener itself, not a guest's (nonexistent) saved views
  const viewsConditions = [...conditions, !UserPlan.isUnauthorized(plan)];

  const defaultProduct = accessibleProducts.includes(HycProduct.PRODUCT_ID)
    ? HycProduct.ABBREVIATION
    : IgcProduct.ABBREVIATION;

  const [
    bondScreenerViews,
    bondScreenerConfig,
    breadcrumbs,
  ] = await Promise.all([
    getServerSideBondScreenerViews(context, 'corp', viewsConditions),
    getServerSideBondScreenerConfig(context, 'corp', conditions),
    getServerSideBondscreenerBreadcrumbs(context),
  ]);

  const industries = pageDictionaries.industries as IDictionaryIndustry[];

  const hasActiveView = bondScreenerViews.views.some((view) => view.id === bondScreenerViews.lastActiveViewId);

  const queryParams = hasActiveView ? null : getDefaultQueryFilters({
    query,
    defaults: {
      products: defaultProduct,
      markets: isRestrictedToEuropeanMarket ? EuropeanMarket.ABBREVIATION : UsMarket.ABBREVIATION,
      segments: CORPORATE_SEGMENTS.join(','),
    },
    validators: corporateValidators,
  });

  if (queryParams) {
    return pageRedirectTo(`${ URL.CORPORATE_BONDSCREENER }${ queryParams }`);
  }

  // TODO: remove when the European market opening period ends
  if (isRestrictedToEuropeanMarket && query.markets && query.markets !== EuropeanMarket.ABBREVIATION) {
    const europeanQuery = getQueryString(addQueryParams(query, { markets: EuropeanMarket.ABBREVIATION }));

    return pageRedirectTo(`${ URL.CORPORATE_BONDSCREENER }?${ europeanQuery }`);
  }

  const { activeColumns, activeFilters } = pickInitialBondScreenerColumnsAndFilters(
    bondScreenerViews,
    bondScreenerConfig,
    industries,
    query,
  );

  const initialFilters = isRestrictedToEuropeanMarket
    ? { ...activeFilters, includedMarketIds: [EuropeanMarket.MARKET_ID] }
    : activeFilters;

  const initialTranches = await getServerSideCorporateBondScreenerTranches(
    context,
    conditions,
    activeColumns,
    initialFilters,
    regions,
    isRestrictedToEuropeanMarket,
  );

  return {
    props: {
      initialTranches,
      breadcrumbs,
      bondScreenerConfig,
      bondScreenerViews,
      initialActiveColumnsIds: activeColumns,
      initialFilters,
    },
  };
}, {
  dictionaries: [...COMMON_SCREENER_DICTIONARIES, 'countries', 'rankings', 'listings', 'uopTypes', 'debutTypes'],
  needsLegacyDictionaries: false,
});

interface IProps {
  initialTranches: ICorporateBondScreenerTrancheCollectionResponse,
  bondScreenerConfig: IBondScreenerConfig,
  bondScreenerViews: IBondScreenerViewsResponse<TFilterPayload>,
  initialActiveColumnsIds: string[],
  initialFilters: TFilterPayload,
}

const CorporateBonds: NextPage<IProps> = ({
  initialTranches,
  bondScreenerConfig,
  bondScreenerViews,
  initialActiveColumnsIds,
  initialFilters,
}) => (
  <PageFiltersContextProvider>
    <PageContentLayout>
      <CorporateBondScreenerPage
        initialTranches={ initialTranches }
        bondScreenerConfig={ bondScreenerConfig }
        bondScreenerViews={ bondScreenerViews }
        initialActiveColumnsIds={ initialActiveColumnsIds }
        initialFilters={ initialFilters }
      />
    </PageContentLayout>
  </PageFiltersContextProvider>
);

export default CorporateBonds;
